import { useRef, useState, useEffect, useId } from 'react'
import { Upload, Loader2, Link2 } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/utils/cn'
import {
  BRAND_LOGO_ACCEPT,
  BRAND_LOGO_ASPECT_RATIO,
  BRAND_LOGO_MAX_BYTES,
  BRAND_LOGO_RECOMMENDED,
  BRAND_LOGO_SIDEBAR_DISPLAY,
  BRAND_LOGO_VARIANT_FIELD,
  type BrandLogoVariant,
} from '@/constants/brandLogos'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'
import { isAspectRatioClose, loadImageDimensions } from '@/utils/loadImageDimensions'
import { systemService } from '@/services/systemService'
import { getFriendlyError } from '@/utils/getFriendlyError'

type LogoUploadFieldProps = {
  variant: BrandLogoVariant
  label: string
  value: string | null | undefined
  previewClassName: string
  companyInitial: string
  onChange: (url: string) => void
  helperText?: string
}

export function LogoUploadField({
  variant,
  label,
  value,
  previewClassName,
  companyInitial,
  onChange,
  helperText,
}: LogoUploadFieldProps) {
  const inputId = useId()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null)
  const [aspectWarning, setAspectWarning] = useState(false)
  const [urlDraft, setUrlDraft] = useState(value || '')

  const displaySrc = value ? resolvePublicAssetUrl(value) : ''
  const fieldKey = BRAND_LOGO_VARIANT_FIELD[variant]

  useEffect(() => {
    setUrlDraft(value || '')
  }, [value])

  useEffect(() => {
    if (!displaySrc) {
      setDimensions(null)
      setAspectWarning(false)
      return
    }
    let cancelled = false
    loadImageDimensions(displaySrc)
      .then((dims) => {
        if (cancelled) return
        setDimensions(dims)
        setAspectWarning(!isAspectRatioClose(dims.width, dims.height, BRAND_LOGO_ASPECT_RATIO))
      })
      .catch(() => {
        if (!cancelled) {
          setDimensions(null)
          setAspectWarning(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [displaySrc])

  async function handleFile(file: File) {
    if (!BRAND_LOGO_ACCEPT.split(',').includes(file.type)) {
      toast.error('Formato inválido. Use PNG, JPEG, WebP ou SVG.')
      return
    }
    if (file.size > BRAND_LOGO_MAX_BYTES) {
      toast.error('Arquivo muito grande. Máximo 5 MB.')
      return
    }

    setUploading(true)
    try {
      const { logoUrl } = await systemService.uploadBrandLogo(file, variant)
      onChange(logoUrl)
      setUrlDraft(logoUrl)
      toast.success('Logo enviada com sucesso')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao enviar a logo'))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  function applyUrl() {
    const trimmed = urlDraft.trim()
    onChange(trimmed)
  }

  const previewW = BRAND_LOGO_SIDEBAR_DISPLAY.width
  const previewH = BRAND_LOGO_SIDEBAR_DISPLAY.height

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={inputId} className="text-text-muted text-[11px] font-medium uppercase tracking-wider">
        {label}
      </label>

      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div
          className={cn(
            'rounded-lg border border-white/[0.06] flex items-center justify-center overflow-hidden shrink-0',
            previewClassName,
          )}
          style={{ width: previewW, height: previewH }}
          title={`Pré-visualização no tamanho do menu (${previewW}×${previewH} px)`}
        >
          {displaySrc ? (
            <img
              src={displaySrc}
              alt={label}
              className="max-w-full max-h-full object-contain"
              style={{ width: previewW, height: previewH }}
            />
          ) : (
            <div className="w-10 h-10 rounded-md bg-accent flex items-center justify-center">
              <span className="text-white font-bold text-sm">{companyInitial}</span>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <p className="text-[10px] text-text-muted leading-relaxed">
            Recomendado:{' '}
            <strong className="text-text-secondary font-medium">
              {BRAND_LOGO_RECOMMENDED.width}×{BRAND_LOGO_RECOMMENDED.height} px
            </strong>{' '}
            (proporção {BRAND_LOGO_ASPECT_RATIO}:1). Exibida no menu até{' '}
            {BRAND_LOGO_SIDEBAR_DISPLAY.width}×{BRAND_LOGO_SIDEBAR_DISPLAY.height} px.
          </p>

          {dimensions && (
            <p className="text-[10px] text-text-secondary font-mono">
              Dimensões do arquivo: {dimensions.width}×{dimensions.height} px
              {aspectWarning && (
                <span className="text-amber-400/90 ml-1">
                  — proporção diferente do ideal; pode aparecer com espaço extra.
                </span>
              )}
            </p>
          )}

          <input
            id={inputId}
            ref={fileRef}
            type="file"
            accept={BRAND_LOGO_ACCEPT}
            className="sr-only"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleFile(file)
            }}
          />

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium border transition-colors min-h-[44px]',
                'border-white/[0.06] bg-white/[0.04] text-text-primary hover:border-accent/50 hover:bg-accent/10',
                uploading && 'opacity-50 pointer-events-none',
              )}
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Enviando…' : 'Enviar imagem'}
            </button>
          </div>

          <div className="flex gap-2 items-center">
            <div className="relative flex-1 min-w-0">
              <Link2
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
              />
              <input
                type="text"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                onBlur={applyUrl}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    applyUrl()
                  }
                }}
                placeholder={`URL ou /uploads/... (${fieldKey})`}
                className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-md pl-9 pr-3 py-2 text-xs outline-none focus:border-accent transition-colors"
              />
            </div>
          </div>

          {helperText && <p className="text-[10px] text-text-muted">{helperText}</p>}
        </div>
      </div>
    </div>
  )
}
